import React, { useCallback, useEffect, useRef, useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import {
  useApiClient, BillingStatusOut, Plan, PriceItemOut, PricesOut, SubscriptionOut,
} from '../services/apiClient';
import { captureEvent } from '../lib/analytics';
import './BillingPage.css';

const MARKETING_URL = process.env.REACT_APP_MARKETING_URL || '';
const BACKEND_URL = process.env.REACT_APP_BACKEND_URL ?? 'http://localhost:5000';

const fmtMoney = (p: PriceItemOut, amount = p.amount) =>
  new Intl.NumberFormat(undefined, { style: 'currency', currency: p.currency.toUpperCase() }).format(amount / 100);

const fmtDay = (iso: string | null) => (iso ? new Date(iso).toLocaleDateString() : '—');

function subscriptionLine(sub: SubscriptionOut): string {
  if (sub.source === 'admin_comp') return `Complimentary access until ${fmtDay(sub.current_period_end)}`;
  // A failed card takes priority over the scheduled cancellation message —
  // the rider needs to know payment is broken even if they already asked to cancel.
  if (sub.status === 'past_due') return 'Payment failed — please update your card';
  if (sub.cancel_at_period_end) return `Access ends on ${fmtDay(sub.current_period_end)}`;
  return `Renews on ${fmtDay(sub.current_period_end)}`;
}

const BillingPage: React.FC = () => {
  const api = useApiClient();
  const [searchParams, setSearchParams] = useSearchParams();
  const [status, setStatus] = useState<BillingStatusOut | null>(null);
  const [prices, setPrices] = useState<PricesOut | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [pricesError, setPricesError] = useState<string | null>(null);
  const [actionLoading, setActionLoading] = useState<Plan | 'portal' | null>(null);
  const autoStarted = useRef(false);

  const checkout = searchParams.get('checkout');
  const requestedPlan = searchParams.get('plan');

  const refresh = useCallback(() => {
    setLoading(true);
    setError(null);
    setPricesError(null);
    // Status and prices load independently. A prices outage must never blank
    // the subscription card, "Manage subscription" button, or downloadable
    // Reports — only the plan cards (and their own visible error) depend on it.
    api.getBillingStatus()
      .then(setStatus)
      .catch((e: Error) => setError(e.message))
      .finally(() => setLoading(false));
    api.getPrices()
      .then((p) => {
        // During a deploy the SPA can be newer than the API, whose old /prices
        // shape is {bikefit, pro}. Rendering that would throw and blank the page.
        if (!p?.monthly || !p?.yearly) {
          console.error('Unexpected /api/billing/prices response shape', p);
          setPricesError('Plans are unavailable right now. Please try again in a few minutes.');
          return;
        }
        setPrices(p);
      })
      .catch((e: Error) => setPricesError(e.message));
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => { refresh(); }, [refresh]);

  useEffect(() => {
    if (!checkout) return;
    const t = window.setTimeout(() => {
      setSearchParams((prev) => {
        const next = new URLSearchParams(prev);
        next.delete('checkout');
        return next;
      }, { replace: true });
    }, 8000);
    return () => window.clearTimeout(t);
  }, [checkout, setSearchParams]);

  const startCheckout = async (plan: Plan) => {
    setActionLoading(plan);
    setError(null);
    try {
      captureEvent('checkout_started', { product: `subscription_${plan}` });
      const { checkout_url } = await api.createSubscriptionCheckout(plan);
      window.location.href = checkout_url;
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Something went wrong');
      setActionLoading(null);
    }
  };

  const openPortal = async () => {
    setActionLoading('portal');
    setError(null);
    try {
      const { portal_url } = await api.openBillingPortal();
      window.location.href = portal_url;
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Something went wrong');
      setActionLoading(null);
    }
  };

  const paid = status?.subscription?.source === 'stripe' ? status.subscription : null;

  // Marketing and portal links arrive as /billing?plan=yearly: go straight to
  // Stripe unless the rider already pays (they get the manage card instead).
  useEffect(() => {
    if (autoStarted.current || loading || !status || paid) return;
    if (requestedPlan !== 'monthly' && requestedPlan !== 'yearly') return;
    autoStarted.current = true;
    // Drop ?plan= first so Back from Stripe lands on plain /billing instead of
    // bouncing the rider straight back into checkout.
    setSearchParams((prev) => {
      const next = new URLSearchParams(prev);
      next.delete('plan');
      return next;
    }, { replace: true });
    void startCheckout(requestedPlan);
  }, [loading, status, paid, requestedPlan]); // eslint-disable-line react-hooks/exhaustive-deps

  // Stripe redirects back before its webhook has necessarily reached us. Until
  // the subscription shows up, don't offer the plans the rider just paid for.
  const finishing = checkout === 'success' && !loading && !!status && !paid;
  const statusRefetched = useRef(false);
  useEffect(() => {
    if (!finishing || statusRefetched.current) return;
    const t = window.setTimeout(() => {
      // One re-fetch only — never poll indefinitely.
      statusRefetched.current = true;
      api.getBillingStatus()
        .then(setStatus)
        .catch((e: Error) => setError(e.message));
    }, 3000);
    return () => window.clearTimeout(t);
  }, [finishing]); // eslint-disable-line react-hooks/exhaustive-deps

  const flash =
    finishing ? { type: 'muted' as const, text: 'Finishing your subscription… this can take a few seconds.' }
    : checkout === 'success' ? { type: 'success' as const, text: 'Your subscription is active. Thank you!' }
    : checkout === 'cancelled' ? { type: 'muted' as const, text: 'Checkout was cancelled.' }
    : null;

  const monthlyTimesTwelve = prices ? prices.monthly.amount * 12 : 0;
  const yearlySavingPct = prices && monthlyTimesTwelve > 0
    ? Math.round((1 - prices.yearly.amount / monthlyTimesTwelve) * 100)
    : null;

  return (
    <div className="billing-page">
      <div className="billing-inner">
        <p className="billing-eyebrow">Account</p>
        <h1 className="billing-title">Subscription</h1>

        {flash && (
          <div className={`billing-flash billing-flash--${flash.type}`} role="status">{flash.text}</div>
        )}
        {loading && <p className="billing-muted">Loading…</p>}
        {error && !loading && <p className="billing-error">{error}</p>}

        {!loading && status && (
          <>
            {status.subscription && (
              <div className="billing-card">
                <div className="billing-row">
                  <span className="billing-label">Plan</span>
                  <span className="billing-value">
                    {status.subscription.plan === 'yearly' ? 'Yearly'
                      : status.subscription.plan === 'monthly' ? 'Monthly' : 'Complimentary'}
                  </span>
                </div>
                <p className="billing-muted">{subscriptionLine(status.subscription)}</p>
                {paid && status.stripe_customer_id && (
                  <div className="billing-actions">
                    <button type="button" className="billing-btn billing-btn--secondary"
                      disabled={actionLoading !== null} onClick={openPortal}>
                      {actionLoading === 'portal' ? 'Opening…' : 'Manage subscription'}
                    </button>
                    <Link to="/video-upload" className="billing-btn billing-btn--primary">Start a fit</Link>
                  </div>
                )}
              </div>
            )}

            {status.has_unused_credit && !paid && (
              <p className="billing-muted">
                You still have an unused bike fit credit. It works as before — <Link to="/video-upload" className="billing-link">start a fit</Link>.
              </p>
            )}

            {pricesError && (
              <p className="billing-error">{pricesError}</p>
            )}

            {!paid && !finishing && prices && (
              <div className="billing-plans">
                <div className="billing-plan billing-plan--highlight">
                  <span className="billing-plan-badge">
                    Best value{yearlySavingPct ? ` · save ${yearlySavingPct}%` : ''}
                  </span>
                  <h2 className="billing-plan-name">Yearly</h2>
                  <p className="billing-plan-price">{fmtMoney(prices.yearly)} / year</p>
                  <p className="billing-muted">{fmtMoney(prices.yearly, Math.round(prices.yearly.amount / 12))} per month · re-fit as often as you like, all year</p>
                  <button type="button" className="billing-btn billing-btn--primary"
                    disabled={actionLoading !== null} onClick={() => startCheckout('yearly')}>
                    {actionLoading === 'yearly' ? 'Opening…' : 'Choose yearly'}
                  </button>
                </div>
                <div className="billing-plan">
                  <h2 className="billing-plan-name">Monthly</h2>
                  <p className="billing-plan-price">{fmtMoney(prices.monthly)} / month</p>
                  <p className="billing-muted">Cancel any time</p>
                  <button type="button" className="billing-btn billing-btn--secondary"
                    disabled={actionLoading !== null} onClick={() => startCheckout('monthly')}>
                    {actionLoading === 'monthly' ? 'Opening…' : 'Choose monthly'}
                  </button>
                </div>
              </div>
            )}

            {status.completed_reports.length > 0 && (
              <div className="billing-reports">
                <h3 className="billing-reports-title">Your Reports</h3>
                {status.completed_reports.map((report) => (
                  <div key={report.order_id} className="billing-report-row">
                    <span className="billing-report-date">
                      {report.completed_at ? new Date(report.completed_at).toLocaleDateString() : 'Processing...'}
                    </span>
                    {report.report_url && (
                      <a href={`${BACKEND_URL}${report.report_url}`} target="_blank" rel="noopener noreferrer" className="billing-link">
                        Download PDF
                      </a>
                    )}
                  </div>
                ))}
              </div>
            )}
          </>
        )}

        <p className="billing-footer-note">
          <Link to="/video-upload" className="billing-link">Back to app</Link>
          {MARKETING_URL && (<>{' · '}<a href={MARKETING_URL} className="billing-link">Bikendure website</a></>)}
        </p>
      </div>
    </div>
  );
};

export default BillingPage;
